import React, { forwardRef, useLayoutEffect, useEffect, useRef, useState } from "react";
import List from "../../../../components/List";
import ListItem from "../../../../components/ListItem";
import Avatar from "../../../../components/Avatar";
import { Row, Col } from "react-styled-flexboxgrid";
import Typography from "../../../../components/Typography";
import Popup from "../../../../components/Popup";
import Divider from "../../../../components/Divider";
import IconButton from "../../../../components/IconButton";
import useTheme from "../../../../hooks/useTheme";
import Box from "../../../../components/Box";
import { makeStyles } from "../../../../utils/styles";
import Button from "../../../../components/Button";
import Badge from "../../../../components/Badge";
import Icon from "../../../../components/Icon";
import ScrollArea from "../../../../components/ScrollArea";
import { paths } from "../../../../routes";
import { NOTIFICATIONS_TYPE } from "../../../../utils/constans";
import { StyledNavLink } from "./styled";
import { getImageUrl, getLabelDateCreatedNotification } from "../../../../utils/helpers";
import Loader from "../../../../components/Loader";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles(css => ({
  listHeading: css`
    margin-bottom: 20px;
  `,
  listItem: css`
    position: relative;
    padding: 10px 0;
    cursor: pointer;
  `,
  listAvatar: css`
    margin-top: 5px;
    margin-right: 20px;
  `,
  listText: css`
    flex-shrink: 1;
    flex-grow: 1;
  `,
  button: css`
    margin-top: 20px;
    padding: 0;
    height: auto;
  `,
  unread: css`
    min-width: 6px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-top: 10px;
    margin-left: 10px;
    margin-right: 6px;
    background-color: ${({ theme }) => theme.colors.blue};
  `,
  unreadAndCanceled: css`
    min-width: 6px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-top: 10px;
    margin-left: 10px;
    margin-right: 6px;
    background-color: ${({ theme }) => theme.colors.error};
  `,
  font: css`
    font-size: 11px;
    line-height: 20px;
    font-weight: 300;
  `,
  typography: css`
    margin-top: 10px;
    margin-bottom: 10px;
  `,
  box: css`
    position: relative;
  `
}));

const Notifications = forwardRef(({ loading, showLoader, isNoNotifications, isMoreNotifications, isUnreadNotifications, notifications, onScroll, onClickNotification, onClickReadAllNotifications }, ref) => {
  const { t } = useTranslation()
  const theme = useTheme();
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const [isImportantNotification, setIsImportantNotification] = useState(false)

  const refPopup = useRef(null);

  const handleClickButton = () => setOpen(prevOpen => !prevOpen);
  const handleClickClose = () => setOpen(false);

  useLayoutEffect(() => {
    const handleClickDocument = (event) => {
      const isClickInside = refPopup.current.contains(event.target)
      if (!isClickInside) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('click', handleClickDocument);
    } else {
      document.removeEventListener('click', handleClickDocument);
    }
    return () => {
      document.removeEventListener('click', handleClickDocument);
    }
  }, [open])

  const handleClickNotification = notification => () => {
    setOpen(false);
    onClickNotification(notification);
  }

  useEffect(() => {
    const importantNotification = notifications?.some((el) => el.is_unread && el.html.includes("zrezygnował"))
    setIsImportantNotification(importantNotification)
  }, [notifications])

  return (
    <Popup
      ref={refPopup}
      open={open}
      relatedItem={
        <IconButton
          onClick={handleClickButton}
          backgroundColor={open ? theme.colors.white : null}
        >
          <Badge
            invisible={!isUnreadNotifications}
            red = {isImportantNotification}
          >
            <Icon
              name="notification"
              height="16"
              width="14"
            />
          </Badge>
        </IconButton>
      }
      positionVertical="top"
      positionHorizontal="left"
      width="450px"
    >
      <Box customStyle={styles.listHeading}>
        <Row middle="xs" between="xs">
          <Col>
            <Typography component={'h2'}>{t("Powiadomienia")}</Typography>
          </Col>
          <Col>
            <IconButton
              onClick={handleClickClose}
              size="small"
            >
              <Icon
                name="close"
                color={theme.colors.darkGrey}
                height="10"
                width="10"
              />
            </IconButton>
          </Col>
        </Row>
      </Box>
      <Divider />
      <Box customStyle={styles.box}>
        <ScrollArea
          heightModifiers={{
            isCalc: true,
            offset: 100,
            maxHeight: 340
          }}
          onScroll={onScroll}
          ref={ref}
        >
          <List>
            {
              notifications && notifications.map(notification => (
                <ListItem
                  key={notification.id}
                  justifyContent="flex-start"
                  customStyle={styles.listItem}
                  noBoldLastChild
                  onClick={handleClickNotification(notification)}
                >
                  <Box customStyle={styles.listAvatar}>
                    <Avatar
                      size="small"
                      src={getImageUrl(notification?.avatar_url)}
                    />
                  </Box>
                  <Box
                    customStyle={styles.listText}
                  >
                    {
                      notification?.html &&
                      <Typography dangerouslySetInnerHTML={{ __html: notification.html }} />
                    }
                    {
                      notification?.created_at &&
                      <Typography
                        as="span"
                        customStyle={styles.font}
                        color={theme.colors.blue}
                      >
                        {getLabelDateCreatedNotification(notification.created_at)}
                      </Typography>
                    }
                  </Box>
                  {
                    notification?.is_unread ? <Box customStyle={notification?.html.includes("zrezygnował") ? styles.unreadAndCanceled : styles.unread} /> : null
                  }
                  {
                    (notification.type === NOTIFICATIONS_TYPE.CHAT_MESSAGE && notification?.data?.userId) &&
                    <StyledNavLink
                      to={`${paths.chat}/${notification?.data?.userId}`}
                    />
                  }
                  {
                    ((notification.type === NOTIFICATIONS_TYPE.NEW_APPLICATION || notification.type === NOTIFICATIONS_TYPE.CANCEL_APPLICATION_BY_CANDIDATE) && notification?.data?.jobId) &&
                    <StyledNavLink
                      to={`${paths.jobs}/${notification?.data?.jobId}`}
                    />
                  }
                </ListItem>
              ))
            }
          </List>
          {
            loading &&
            <Typography align="center" customStyle={styles.typography}>{t("Ładowanie")}</Typography>
          }
          {
            (!loading && !isNoNotifications && !isMoreNotifications) &&
            <Typography align="center" customStyle={styles.typography}>{t("Nie ma więcej powiadomień")}</Typography>
          }
          {
            (!loading && isNoNotifications) &&
            <Typography align="center" customStyle={styles.typography}>{t("Brak powiadomień")}</Typography>
          }
        </ScrollArea>
        {
          (!isNoNotifications && isUnreadNotifications) &&
          <>
            <Divider />
            <Button
              variant="text"
              onClick={onClickReadAllNotifications}
              customStyle={styles.button}
            >
              {t("Oznacz wszystkie jako przeczytane")}
          </Button>
          </>
        }
        <Loader show={showLoader} />
      </Box>
    </Popup>
  );
});

export default React.memo(Notifications);
